import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import '../styles/admin.css';

const ADMIN_TOKEN_STORAGE_KEY = 'massal_admin_token';
const DEFAULT_ORDER_STATUS = 'pending_review';
const DEFAULT_PAYMENT_STATUS = 'not_charged';

const AdminPage = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [adminToken, setAdminToken] = useState(() => sessionStorage.getItem(ADMIN_TOKEN_STORAGE_KEY) || '');
  const [tokenInput, setTokenInput] = useState(() => sessionStorage.getItem(ADMIN_TOKEN_STORAGE_KEY) || '');
  const [authRequired, setAuthRequired] = useState(false);
  const { t } = useTranslation();

  const fetchOrders = useCallback(async (token = adminToken) => {
    if (!token) {
      setAuthRequired(true);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setAuthRequired(false);
      console.log('🔍 Fetching orders from database...');

      const response = await axios.get('/api/admin/get-orders', {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.data.success) {
        setOrders(response.data.orders);
        setError('');
        console.log(`✅ Loaded ${response.data.totalOrders} orders`);
      } else {
        setError(t('adminPage.fetchFailed'));
      }
    } catch (err) {
      console.error('❌ Error fetching orders:', err);

      if (err.response?.status === 401) {
        sessionStorage.removeItem(ADMIN_TOKEN_STORAGE_KEY);
        setAdminToken('');
        setTokenInput('');
        setAuthRequired(true);
        setError('');
      } else {
        setError(t('adminPage.connectionError'));
      }
    } finally {
      setLoading(false);
    }
  }, [adminToken, t]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const handleAdminLogin = (e) => {
    e.preventDefault();

    const token = tokenInput.trim();
    if (!token) {
      setAuthRequired(true);
      return;
    }

    sessionStorage.setItem(ADMIN_TOKEN_STORAGE_KEY, token);
    setAdminToken(token);
    setAuthRequired(false);
    setLoading(true);
  };

  const handleAdminLogout = () => {
    sessionStorage.removeItem(ADMIN_TOKEN_STORAGE_KEY);
    setAdminToken('');
    setTokenInput('');
    setOrders([]);
    setAuthRequired(true);
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return Number.isFinite(date.getTime())
      ? date.toLocaleString()
      : t('adminPage.dateUnavailable');
  };

  const formatPrice = (price) => {
    if (price === null || price === undefined || price === '') {
      return t('adminPage.pricePending');
    }

    return `$${Number(price || 0).toFixed(2)}`;
  };

  const formatAge = (age) => (
    age
      ? t('adminPage.values.ageYears', { age })
      : t('adminPage.values.notProvided')
  );

  const formatUploadedCount = (count) => {
    const numericCount = Number(count || 0);
    return t('adminPage.values.uploadedCount', {
      count: Number.isFinite(numericCount) ? numericCount : 0
    });
  };

  const formatStatus = (status) => (
    t(`adminPage.statusLabels.${status || DEFAULT_ORDER_STATUS}`, {
      defaultValue: status || t('adminPage.statusPending')
    })
  );

  const formatPaymentStatus = (status) => (
    t(`adminPage.paymentStatusLabels.${status || DEFAULT_PAYMENT_STATUS}`, {
      defaultValue: status || t('adminPage.paymentStatusLabels.not_charged')
    })
  );

  const formatRequestLanguage = (locale) => (
    t(`adminPage.languageLabels.${locale || 'en'}`, {
      defaultValue: locale || t('adminPage.languageLabels.en')
    })
  );

  const renderOptionalField = (label, value) => (
    value ? <p><strong>{label}</strong> {value}</p> : null
  );

  const renderImageLinks = (urls = [], labelKey) => {
    if (!urls.length) {
      return <p>{t('adminPage.noUploadedImages')}</p>;
    }

    return (
      <div className="admin-image-links">
        {urls.map((url, index) => (
          <a
            key={url}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="admin-image-link"
          >
            {t(labelKey, { count: index + 1 })}
          </a>
        ))}
      </div>
    );
  };

  if (authRequired || !adminToken) {
    return (
      <div className="admin-page">
        <div className="container">
          <div className="admin-auth-panel">
            <h1>{t('adminPage.auth.title')}</h1>
            <p>{t('adminPage.auth.message')}</p>
            <form onSubmit={handleAdminLogin} className="admin-auth-form">
              <label htmlFor="admin-token">{t('adminPage.auth.tokenLabel')}</label>
              <input
                id="admin-token"
                type="password"
                value={tokenInput}
                onChange={(e) => setTokenInput(e.target.value)}
                autoComplete="off"
                placeholder={t('adminPage.auth.tokenPlaceholder')}
              />
              <button type="submit" className="btn-primary">
                {t('adminPage.auth.submit')}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="admin-page">
        <div className="container">
          <div className="loading">
            <h2>{t('adminPage.loading.title')}</h2>
            <p>{t('adminPage.loading.message')}</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-page">
        <div className="container">
          <div className="error">
            <h2>{t('adminPage.error.title')}</h2>
            <p>{error}</p>
            <button onClick={fetchOrders} className="btn-primary">
              {t('common.buttons.tryAgain')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="container">
        <div className="admin-header">
          <h1>{t('adminPage.headerTitle')}</h1>
          <p>{t('adminPage.totalOrders')} <strong>{orders.length}</strong></p>
          <div className="admin-header-actions">
            <button onClick={() => fetchOrders()} className="btn-secondary">
              {t('common.buttons.refresh')}
            </button>
            <button onClick={handleAdminLogout} className="btn-secondary">
              Sign Out
            </button>
          </div>
        </div>

        {orders.length === 0 ? (
          <div className="no-orders">
            <h2>{t('adminPage.noOrders.title')}</h2>
            <p>{t('adminPage.noOrders.message')}</p>
          </div>
        ) : (
          <div className="orders-grid">
            {orders.map((order) => (
              <div
                key={order.id}
                className="order-card"
                onClick={() => setSelectedOrder(order)}
              >
                <div className="order-header">
                  <h3>{order.childName}</h3>
                  <span className="order-date">{formatDate(order.timestamp)}</span>
                </div>

                <div className="order-details">
                  <p><strong>{t('adminPage.labels.age')}</strong> {formatAge(order.childAge)}</p>
                  {order.childNeed && (
                    <p><strong>{t('adminPage.labels.need')}</strong> {order.childNeed}</p>
                  )}
                  {order.customNeedDetail && (
                    <p><strong>{t('adminPage.labels.customNeed')}</strong> {order.customNeedDetail}</p>
                  )}
                  <p><strong>{t('adminPage.labels.theme')}</strong> {order.storyTheme}</p>
                  <p><strong>{t('adminPage.labels.parent')}</strong> {order.parentName}</p>
                  <p><strong>{t('adminPage.labels.email')}</strong> {order.parentEmail}</p>
                  <p><strong>{t('adminPage.labels.delivery')}</strong> {order.deliveryLabel || order.deliveryPreference}</p>
                  <p><strong>{t('adminPage.labels.price')}</strong> {formatPrice(order.price)}</p>
                  {order.estimatedPrice !== null && order.estimatedPrice !== undefined && (
                    <p><strong>{t('adminPage.labels.estimatedPrice')}</strong> {formatPrice(order.estimatedPrice)}</p>
                  )}
                  <p><strong>{t('adminPage.labels.paymentStatus')}</strong> {formatPaymentStatus(order.paymentStatus)}</p>
                </div>

                <div className="order-stats">
                  <span className="stat">{t('adminPage.stats.childPhotos', { count: order.childPhotosCount })}</span>
                  <span className="stat">{t('adminPage.stats.inspiration', { count: order.inspirationImagesCount })}</span>
                </div>

                <div className="order-id">
                  {t('adminPage.labels.orderId')} {order.id.substring(0, 8)}...
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Order Detail Modal */}
        {selectedOrder && (
          <div className="modal-overlay" onClick={() => setSelectedOrder(null)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{t('adminPage.orderDetailsTitle', { name: selectedOrder.childName })}</h2>
                <button
                  className="close-btn"
                  onClick={() => setSelectedOrder(null)}
                >
                  ✕
                </button>
              </div>

              <div className="modal-body">
                <div className="detail-section">
                  <h3>{t('adminPage.orderInfo')}</h3>
                  <p><strong>{t('adminPage.labels.orderId')}</strong> {selectedOrder.id}</p>
                  <p><strong>{t('adminPage.labels.date')}</strong> {formatDate(selectedOrder.timestamp)}</p>
                  <p><strong>{t('adminPage.labels.requestLanguage')}</strong> {formatRequestLanguage(selectedOrder.requestLocale)}</p>
                  <p><strong>{t('adminPage.labels.status')}</strong> {formatStatus(selectedOrder.status)}</p>
                  <p><strong>{t('adminPage.labels.paymentStatus')}</strong> {formatPaymentStatus(selectedOrder.paymentStatus)}</p>
                  <p><strong>{t('adminPage.labels.price')}</strong> {formatPrice(selectedOrder.price)}</p>
                  {selectedOrder.estimatedPrice !== null && selectedOrder.estimatedPrice !== undefined && (
                    <p><strong>{t('adminPage.labels.estimatedPrice')}</strong> {formatPrice(selectedOrder.estimatedPrice)}</p>
                  )}
                </div>

                <div className="detail-section">
                  <h3>{t('adminPage.childInfo')}</h3>
                  <p><strong>{t('contactPage.fields.name')}</strong> {selectedOrder.childName}</p>
                  <p><strong>{t('adminPage.labels.age')}</strong> {formatAge(selectedOrder.childAge)}</p>
                  {renderOptionalField(t('adminPage.labels.gender'), selectedOrder.childGenderLabel || selectedOrder.childGender)}
                  {renderOptionalField(t('adminPage.labels.personality'), selectedOrder.childPersonality)}
                  {renderOptionalField(t('adminPage.labels.interests'), selectedOrder.favoriteActivities)}
                </div>

                <div className="detail-section">
                  <h3>{t('adminPage.storyDetails')}</h3>
                  {selectedOrder.childNeed && (
                    <p><strong>{t('adminPage.labels.need')}</strong> {selectedOrder.childNeed}</p>
                  )}
                  {renderOptionalField(t('adminPage.labels.customNeed'), selectedOrder.customNeedDetail)}
                  <p><strong>{t('adminPage.labels.theme')}</strong> {selectedOrder.storyTheme}</p>
                  {renderOptionalField(t('adminPage.labels.customStoryWorld'), selectedOrder.customStoryWorldDetail)}
                  {renderOptionalField(t('adminPage.labels.storyLength'), selectedOrder.storyLengthLabel || selectedOrder.storyLength)}
                  {renderOptionalField(t('adminPage.labels.educationalFocus'), selectedOrder.educationalFocusLabel || selectedOrder.educationalFocus)}
                  {renderOptionalField(t('adminPage.labels.messageToReinforce'), selectedOrder.moralLesson)}
                  <p><strong>{t('adminPage.labels.delivery')}</strong> {selectedOrder.deliveryLabel || selectedOrder.deliveryPreference}</p>
                </div>

                <div className="detail-section">
                  <h3>{t('adminPage.characterInfo')}</h3>
                  {renderOptionalField(t('adminPage.labels.mainCharacter'), selectedOrder.mainCharacterTypeLabel || selectedOrder.mainCharacterType)}
                  {renderOptionalField(t('adminPage.labels.characterTraits'), selectedOrder.characterTraits)}
                  {renderOptionalField(t('adminPage.labels.sidekick'), selectedOrder.sidekickCharacter)}
                </div>

                <div className="detail-section">
                  <h3>{t('adminPage.contactInfo')}</h3>
                  <p><strong>{t('adminPage.labels.parent')}</strong> {selectedOrder.parentName}</p>
                  <p><strong>{t('adminPage.labels.email')}</strong> {selectedOrder.parentEmail}</p>
                  {selectedOrder.phoneNumber && (
                    <p><strong>{t('adminPage.labels.phone')}</strong> {selectedOrder.phoneNumber}</p>
                  )}
                </div>

                <div className="detail-section">
                  <h3>{t('adminPage.images')}</h3>
                  <p><strong>{t('adminPage.labels.childPhotos')}</strong> {formatUploadedCount(selectedOrder.childPhotosCount)}</p>
                  {renderImageLinks(selectedOrder.childPhotos, 'adminPage.imageLinks.childPhoto')}
                  <p><strong>{t('adminPage.labels.inspirationImages')}</strong> {formatUploadedCount(selectedOrder.inspirationImagesCount)}</p>
                  {renderImageLinks(selectedOrder.inspirationImages, 'adminPage.imageLinks.inspirationImage')}
                </div>

                {selectedOrder.specialRequests && (
                  <div className="detail-section">
                    <h3>{t('adminPage.specialRequests')}</h3>
                    <p>{selectedOrder.specialRequests}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPage;
